<template>
  <div class="expanded-row-content bg-gray-50 border-t border-gray-200">
    <div class="p-6">
      <!-- Controls Header -->
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-semibold text-gray-900">
          Customer Details
        </h3>
        <div class="flex items-center space-x-2">
          <button
            class="text-sm px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
            @click="impersonateOrganization">
            <i class="fas fa-user-secret mr-2" />
            Impersonate Customer
          </button>
          <button
            class="text-sm px-3 py-1 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            @click="toggleAllCards">
            <i
              :class="allExpanded ? 'fas fa-compress-alt' : 'fas fa-expand-alt'"
              class="mr-1" />
            {{ allExpanded ? 'Collapse All' : 'Expand All' }}
          </button>
        </div>
      </div>

      <!-- Spotlight Tracking Card -->
      <CollapsibleCard
        ref="spotlightCard"
        :default-expanded="true"
        class="mb-4">
        <template #title>
          <i class="fas fa-broadcast-tower text-purple-600 mr-2" />
          Spotlight Pages Configuration
        </template>
        <SpotlightTracking :organization-id="organizationId" />
      </CollapsibleCard>

      <!-- GeoGrid Intelligence -->
      <CollapsibleCard
        ref="geogridCard"
        :default-expanded="true"
        class="mb-4">
        <template #title>
          <i class="fas fa-map-marked-alt text-yellow-600 mr-2" />
          GeoGrid Intelligence
        </template>
        <GeogridIntelligenceCard :organization-id="organizationId" />
      </CollapsibleCard>

      <!-- Locations -->
      <CollapsibleCard
        ref="locationsCard"
        :default-expanded="false"
        class="mb-4">
        <template #title>
          <i class="fas fa-map-marker-alt text-green-600 mr-2" />
          Locations
        </template>
        <LocationsList :organization-id="organizationId" />
      </CollapsibleCard>

      <!-- Primary Contact (Full width) -->
      <CollapsibleCard
        ref="contactCard"
        :default-expanded="false"
        class="mb-4">
        <template #title>
          <i class="fas fa-user text-blue-600 mr-2" />
          Primary Contact
        </template>
        <PrimaryContactForm :organization-id="organizationId" />
      </CollapsibleCard>

      <!-- Checkins Analytics with Charts (Full width) -->
      <CollapsibleCard
        ref="checkinsCard"
        :default-expanded="true"
        class="mb-4">
        <template #title>
          <i class="fas fa-map-pin text-rose-600 mr-2" />
          Checkins Analytics
        </template>
        <CheckinsAnalyticsCard :organization-id="organizationId" />
      </CollapsibleCard>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import CollapsibleCard from '../collapsible_card.vue'
import SpotlightTracking from '../spotlight_tracking.vue'
import GeogridIntelligenceCard from '../geogrid_intelligence_card.vue'
import LocationsList from './locations_list.vue'
import PrimaryContactForm from '../primary_contact_form.vue'
import CheckinsAnalyticsCard from '../checkins_analytics_card.vue'

export default {
  name: 'ResellerOrganizationExpandedRow',

  components: {
    CollapsibleCard,
    SpotlightTracking,
    GeogridIntelligenceCard,
    LocationsList,
    PrimaryContactForm,
    CheckinsAnalyticsCard
  },

  props: {
    organizationId: {
      type: String,
      required: true
    },
    organization: {
      type: Object,
      required: true
    }
  },

  setup(props) {
    const spotlightCard = ref(null)
    const geogridCard = ref(null)
    const locationsCard = ref(null)
    const contactCard = ref(null)
    const checkinsCard = ref(null)
    const allExpanded = ref(false)

    const toggleAllCards = () => {
      const cards = [
        spotlightCard.value,
        geogridCard.value,
        locationsCard.value,
        contactCard.value,
        checkinsCard.value
      ].filter(Boolean)

      allExpanded.value = !allExpanded.value
      cards.forEach((card) => {
        if (card && card.setExpanded) {
          card.setExpanded(allExpanded.value)
        }
      })
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    const impersonateOrganization = async() => {
      if (!confirm(`Are you sure you want to impersonate ${props.organization.name}? You will be logged in as their account.`)) {
        return
      }

      try {
        const response = await fetch(`/ajax/reseller/organizations/${props.organizationId}/impersonate`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          credentials: 'same-origin'
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to impersonate organization')
        }

        const data = await response.json()

        // Redirect to the appropriate dashboard
        if (data.success && data.redirect_url) {
          window.location.href = data.redirect_url
        } else {
          throw new Error('Impersonation failed')
        }
      } catch (err) {
        console.error('Error impersonating organization:', err)
        alert(`Failed to impersonate organization: ${err.message}`)
      }
    }

    return {
      spotlightCard,
      geogridCard,
      locationsCard,
      contactCard,
      checkinsCard,
      allExpanded,
      toggleAllCards,
      formatDate,
      impersonateOrganization
    }
  }
}
</script>

<style scoped>
.expanded-row-content {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
